import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as AntApp } from 'antd';
import AppBar from '../components/AppBar';
import { AsyncButton, EmptyState, InfiniteSentinel, Skeletons, I, dt } from '../components/kit';
import { usePaginatedList } from '../hooks/usePaginatedList';
import * as notifsApi from '../api/notifications';
import { refreshUnreadCount } from '../components/NotifBell';
import { usePushPrompt } from '../context/PushPromptContext';

// One flat, newest-first feed — no category sections and no per-category mutes.
// Every notification the servers dispatch is delivered and shown here; the only
// switch left is the device-level push subscription (the Enable banner below).

// colour of the leading dot per notification type
const TYPE_TONE = {
  signing: 'var(--accent)', booking: 'var(--accent)', listing: 'var(--accent)',
  // Co-share: another agent asking to work one of your listings, or the sale that
  // came out of it. Purple so it reads as its own thing in the feed — it is the one
  // notification that is about a colleague rather than a record.
  co_share: 'var(--purple)',
  payout: 'var(--ok)', earning: 'var(--ok)', commission: 'var(--ok)', paid: 'var(--ok)', approval: 'var(--ok)',
  rejection: 'var(--bad)', voided: 'var(--bad)',
  reminder: 'var(--warn)', expiry: 'var(--warn)',
  system: 'var(--muted)',
};
const toneOf = (t) => TYPE_TONE[t] || 'var(--primary)';

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' };

export default function NotifsPage() {
  const nav = useNavigate();
  const { message } = AntApp.useApp();
  // The manual route into push, kept for everyone who dismissed the sign-in ask
  // (or was never asked — an iPhone still running in a browser tab). Reads the
  // same device-level state the sheet does, so the two can never disagree.
  const { supported, subscribed, blocked, enable } = usePushPrompt();
  const [readIds, setReadIds] = useState(() => new Set());

  const list = usePaginatedList(notifsApi.listNotifications, { limit: 10 });

  const enablePush = async () => {
    await enable();
    message.success('Push notifications enabled');
  };

  const isUnread = (n) => !n.read_at && !readIds.has(n.id);
  const hasUnread = list.items.some(isUnread);

  const markAllRead = async () => {
    await notifsApi.patchNotificationsReadAll();
    setReadIds((prev) => {
      const s = new Set(prev);
      list.items.forEach((n) => s.add(n.id));
      return s;
    });
    refreshUnreadCount();
    message.success('All notifications marked as read');
  };

  const openNotif = async (n) => {
    if (isUnread(n)) {
      try {
        await notifsApi.patchNotificationsReadById(n.id);
        setReadIds((prev) => new Set(prev).add(n.id));
        refreshUnreadCount();
      } catch (e) {
        message.error(e.message);
      }
    }
    if (n.deep_link) nav(n.deep_link);
  };

  const anyItems = list.items.length > 0;

  const renderRow = (n, last) => {
    const unread = isUnread(n);
    return (
      <div
        key={n.id}
        onClick={() => openNotif(n)}
        style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '14px 16px', borderBottom: last ? 'none' : '1px solid var(--border)', cursor: 'pointer', background: unread ? 'color-mix(in srgb,var(--primary) 5%,transparent)' : 'transparent' }}
      >
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: toneOf(n.type), flex: 'none', marginTop: 4 }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: unread ? 700 : 500 }}>{n.title}</div>
            {unread && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--primary)', flex: 'none' }} />}
          </div>
          {n.body && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{n.body}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 'none', color: 'var(--muted)' }}>
          <div style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>{dt(n.at)}</div>
          <span style={{ display: 'flex' }}><I.chevR /></span>
        </div>
      </div>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Notifications" sub="tap a row to open it" />

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Enable push — only once the probe confirms this device has no
            subscription. When the agent has already denied at the OS level there
            is no button that can help: the in-app call would throw every time, so
            the row explains the only route that actually works instead. */}
        {supported && subscribed === false && (blocked ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'color-mix(in srgb,var(--warn) 12%,transparent)', border: '1px solid color-mix(in srgb,var(--warn) 30%,transparent)', borderRadius: 12, padding: '14px 15px' }}>
            <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.45 }}>
              Notifications are blocked for this app. Allow them in your browser or phone settings, then come back here.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--chip)', borderRadius: 12, padding: '14px 15px' }}>
            <div style={{ flex: 1, fontSize: 12.5, color: 'var(--primary)', fontWeight: 500 }}>Enable push notifications to hear about signings &amp; payouts instantly</div>
            <AsyncButton type="primary" size="small" style={{ height: 30, fontWeight: 600, flex: 'none' }} onClick={enablePush} onError={(e) => message.error(e.message)}>Enable</AsyncButton>
          </div>
        ))}

        {list.loading && !anyItems ? (
          <Skeletons n={5} height={62} />
        ) : !anyItems ? (
          <EmptyState title="All caught up" sub="New signings, payouts and reminders will show up here." />
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>Newest first</div>
              {hasUnread && (
                <AsyncButton type="text" size="small" style={{ height: 24, padding: '0 4px', color: 'var(--primary)', fontWeight: 600, fontSize: 12.5 }} onClick={markAllRead} onError={(e) => message.error(e.message)}>
                  Mark all read
                </AsyncButton>
              )}
            </div>
            <div style={card}>
              {list.items.map((n, i) => renderRow(n, i === list.items.length - 1))}
            </div>
            <InfiniteSentinel hasMore={list.hasMore} loading={list.loadingMore} onReach={list.loadMore} />
          </>
        )}

        <div style={{ height: 6 }} />
      </div>
    </div>
  );
}
